import React from 'react';
import { Box, Button, LinearProgress } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useAudioRecognition } from '../AudioRecognitionContext';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';

type CalibrationPanelProps = {
  title?: string;
  description?: string;
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
};

const calibrationPrimaryButtonSx = {
  minHeight: '42px',
  paddingX: '18px',
  borderRadius: '12px',
  backgroundColor: '#469F6A',
  color: '#FFFFFF',
  fontSize: '14px',
  fontWeight: 700,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#3a8556',
  },
  '&.Mui-disabled': {
    backgroundColor: '#767C8C',
    color: '#ADB1B7',
  },
};

const calibrationSecondaryButtonSx = {
  minHeight: '42px',
  paddingX: '18px',
  borderRadius: '12px',
  borderColor: '#767C8C',
  color: '#ECECED',
  fontSize: '14px',
  fontWeight: 700,
  textTransform: 'none',
  '&:hover': {
    borderColor: '#6F87F1',
    backgroundColor: 'rgba(111, 135, 241, 0.08)',
  },
  '&.Mui-disabled': {
    borderColor: 'rgba(118, 124, 140, 0.45)',
    color: 'rgba(173, 177, 183, 0.6)',
  },
};

const calibrationDangerButtonSx = {
  ...calibrationSecondaryButtonSx,
  '&:hover': {
    borderColor: '#C24444',
    backgroundColor: 'rgba(194, 68, 68, 0.08)',
  },
};

const CalibrationPanel: React.FC<CalibrationPanelProps> = ({
  title = 'Acoustic Piano Calibration',
  description = 'Sample your piano, room, and mic so acoustic note recognition is biased toward the notes this instrument actually produces.',
  sx,
  children,
}) => {
  const audioRecognitionEnabled = useFeatureFlag(FeatureFlag.USE_AUDIO_RECOGNITION);
  const {
    calibration,
    calibrationProfile,
    startCalibration,
    cancelCalibration,
    clearCalibrationProfile,
  } = useAudioRecognition();

  return (
    <Box
      sx={{
        marginTop: '22px',
        marginBottom: '4px',
        marginLeft: '28px',
        marginRight: '28px',
        padding: '18px 18px 16px 18px',
        borderRadius: '18px',
        border: '1px solid rgba(111, 135, 241, 0.28)',
        background:
          'linear-gradient(180deg, rgba(17, 31, 58, 0.96) 0%, rgba(8, 15, 28, 0.98) 100%)',
        boxShadow: '0px 16px 28px rgba(0, 0, 0, 0.22)',
        ...sx,
      }}
    >
      <Box
        sx={{
          color: '#F2F6FF',
          fontSize: '17px',
          fontWeight: 700,
          letterSpacing: '0.03em',
          marginBottom: '6px',
        }}
      >
        {title}
      </Box>
      <Box
        sx={{
          color: 'rgba(226, 234, 255, 0.78)',
          fontSize: '12px',
          lineHeight: 1.5,
          marginBottom: children ? '10px' : '14px',
        }}
      >
        {description}
      </Box>

      {children}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          marginBottom: '12px',
          marginTop: children ? '6px' : 0,
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            borderRadius: '999px',
            backgroundColor: calibration.running
              ? 'rgba(125, 255, 165, 0.14)'
              : calibrationProfile
                ? 'rgba(111, 135, 241, 0.18)'
                : 'rgba(255, 210, 125, 0.12)',
            color: calibration.running ? '#7DFFA5' : calibrationProfile ? '#C4D4FF' : '#FFD27D',
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          {calibration.running
            ? `Calibrating • Step ${calibration.stepIndex}/${Math.max(calibration.totalSteps, 1)}`
            : calibrationProfile
              ? calibrationProfile.label
              : 'No Profile'}
        </Box>

        {calibrationProfile && (
          <Box sx={{ color: 'rgba(226, 234, 255, 0.68)', fontSize: '11px' }}>
            {calibrationProfile.noteTemplates.length} sampled note
            {calibrationProfile.noteTemplates.length === 1 ? '' : 's'}
          </Box>
        )}
      </Box>

      {audioRecognitionEnabled ? (
        <Box
          sx={{
            marginBottom: calibration.running ? '12px' : '14px',
            borderRadius: '14px',
            backgroundColor: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.06)',
            padding: '14px 14px 12px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: '10px',
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '7px 11px',
                borderRadius: '999px',
                backgroundColor: calibration.running
                  ? calibration.stageLabel === 'Capture'
                    ? 'rgba(125, 255, 165, 0.14)'
                    : 'rgba(121, 216, 255, 0.14)'
                  : 'rgba(255,255,255,0.07)',
                color: calibration.running
                  ? calibration.stageLabel === 'Capture'
                    ? '#7DFFA5'
                    : '#79D8FF'
                  : '#D9E5FF',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {calibration.stageLabel}
            </Box>

            {calibration.timerLabel && (
              <Box
                sx={{
                  color: '#DDE7FF',
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {calibration.timerLabel}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              color: '#F2F6FF',
              fontSize: '14px',
              fontWeight: 700,
              lineHeight: 1.45,
              marginBottom: '6px',
            }}
          >
            {calibration.instruction}
          </Box>

          <Box
            sx={{
              color: 'rgba(226, 234, 255, 0.78)',
              fontSize: '12px',
              lineHeight: 1.6,
              minHeight: '38px',
            }}
          >
            {calibration.detail}
          </Box>

          {calibration.heardNotes.length > 0 && (
            <Box
              sx={{
                marginTop: '10px',
                color: '#B9D4FF',
                fontSize: '11px',
                letterSpacing: '0.02em',
              }}
            >
              Hearing: {calibration.heardNotes.join(', ')}
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            color: '#FFB4B4',
            fontSize: '12px',
            lineHeight: 1.5,
            minHeight: '36px',
            marginBottom: calibration.running ? '10px' : '14px',
          }}
        >
          Audio recognition input is turned off in Experimental Features, so calibration is
          unavailable.
        </Box>
      )}

      {calibration.running && (
        <Box sx={{ marginBottom: '14px' }}>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, calibration.progress * 100))}
            sx={{
              height: '8px',
              borderRadius: '999px',
              backgroundColor: 'rgba(255,255,255,0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: '999px',
                background: 'linear-gradient(90deg, #79D8FF 0%, #7DFFA5 100%)',
              },
            }}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          disabled={!audioRecognitionEnabled || calibration.running}
          onClick={() => {
            void startCalibration().catch(() => {
              // Calibration state already reports failures.
            });
          }}
          sx={calibrationPrimaryButtonSx}
        >
          Run Calibration
        </Button>

        <Button
          variant="outlined"
          disabled={!calibration.running}
          onClick={() => {
            cancelCalibration();
          }}
          sx={calibrationSecondaryButtonSx}
        >
          Cancel
        </Button>

        <Button
          variant="outlined"
          disabled={!calibrationProfile || calibration.running}
          onClick={() => {
            clearCalibrationProfile();
          }}
          sx={calibrationDangerButtonSx}
        >
          Reset Profile
        </Button>
      </Box>
    </Box>
  );
};

export default CalibrationPanel;
